import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import {
  fetchDagMetadata,
  fetchDagRows,
  searchDagRows,
  patchDagRows,
  bulkEditColumn,
  fetchDropdowns,
  reorderDagRow,
} from '@/api/dagTable'
import type {
  BatchPatchRequest,
  BulkColumnEditRequest,
  ReorderLineItemRequest,
} from '@/types/dag'
import type { FilterGroup } from '@/types'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

/**
 * Builds the exact React Query key `useDagRows` uses for a given page, so
 * callers that need to read/write that cache entry directly (e.g. optimistic
 * row-reorder updates) always target the same entry `useDagRows` populates.
 */
export function getDagRowsQueryKey(
  planId: string,
  versionId: string,
  page: number,
  pageSize: number,
  searchQuery?: string,
  filter?: FilterGroup | null
) {
  const offset = (page - 1) * pageSize
  const hasFilter = !!filter && filter.conditions.length > 0
  return queryKeys.dagTable.rows(
    planId,
    versionId,
    offset,
    pageSize,
    searchQuery,
    hasFilter ? filter : null
  )
}

export function useDagMetadata(
  planId: string | undefined,
  versionId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.dagTable.metadata(planId!, versionId!),
    queryFn: () => fetchDagMetadata(planId!, versionId!),
    enabled: !!planId && !!versionId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDagRows(
  planId: string | undefined,
  versionId: string | undefined,
  page: number,
  pageSize: number,
  searchQuery?: string,
  filter?: FilterGroup | null
) {
  const offset = (page - 1) * pageSize
  const hasFilter = !!filter && filter.conditions.length > 0
  return useQuery({
    queryKey: getDagRowsQueryKey(
      planId!,
      versionId!,
      page,
      pageSize,
      searchQuery,
      filter
    ),
    queryFn: () =>
      hasFilter
        ? searchDagRows(planId!, versionId!, {
            filter,
            offset,
            limit: pageSize,
            include_aggregates: true,
            q: searchQuery || undefined,
          })
        : fetchDagRows(planId!, versionId!, offset, pageSize, searchQuery),
    enabled: !!planId && !!versionId,
    placeholderData: keepPreviousData,
    // Product edits can change whether a row matches the current search/filter,
    // so never serve a cached result for these params — always hit the server.
    staleTime: 0,
  })
}

export function usePatchDagRows(
  planId: string | undefined,
  versionId: string | undefined
) {
  return useMutation({
    mutationFn: (payload: BatchPatchRequest) =>
      patchDagRows(planId!, versionId!, payload),
    onError: (error: Error) => {
      toast.error(getErrorMessage(error, 'Failed to update cell'))
    },
  })
}

export function useBulkColumnEdit(
  planId: string | undefined,
  versionId: string | undefined
) {
  return useMutation({
    mutationFn: (payload: BulkColumnEditRequest) =>
      bulkEditColumn(planId!, versionId!, payload),
  })
}

export function useReorderDagRow(
  planId: string | undefined,
  versionId: string | undefined
) {
  return useMutation({
    mutationFn: (vars: {
      lineId: string
      rowVersion: number
      anchor: ReorderLineItemRequest
    }) =>
      reorderDagRow(
        planId!,
        versionId!,
        vars.lineId,
        vars.rowVersion,
        vars.anchor
      ),
    onError: (error: Error) => {
      toast.error(getErrorMessage(error, 'Failed to reorder row'))
    },
  })
}

export function useDropdownLookups() {
  return useQuery({
    queryKey: queryKeys.dropdowns.all,
    queryFn: fetchDropdowns,
    staleTime: Infinity,
  })
}
